import React, { useState, useEffect } from 'react';
import { FaSearch, FaChartBar, FaGlobe, FaCheckCircle, FaClock, FaExclamationTriangle, FaTimes, FaBrain } from 'react-icons/fa';
import { fetchQueryLogDashboard, fetchQueryLogAnalytics, fetchQueryLogs } from '../../services/service';
import { useAsgardeoAuth } from '../../hooks/useAsgardeoAuth';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const QueryDashboard = () => {
  const [stats, setStats] = useState({
    totalQueries: 0,
    completedQueries: 0,
    pendingQueries: 0,
    failedQueries: 0,
    averageConfidence: 0,
    topQueryTypes: [
      { type: 'Top query types', count: 0, value: 0 }
    ],
    recentActivity: [],
    // New stats for enhanced dashboard
    recent7DaysCount: 0,
    monthlyCount: 0,
    queryTypeStats: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState({
    totalQueries: 0,
    totalSuccess: 0,
    totalFailed: 0,
    perDay: []
  });
  const [chartData, setChartData] = useState({
    dailyChart: null,
    hourlyFailedChart: null
  });

  const auth = useAsgardeoAuth();

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load dashboard stats
      const dashboardData = await fetchQueryLogDashboard(auth);
      console.log('dashboardData:', dashboardData);
      
      // Load analytics data
      const analyticsData = await fetchQueryLogAnalytics(auth);

      // Fetch ALL query logs to calculate statistics from total data
      const allLogsResponse = await fetchQueryLogs({ 
        page: 1, 
        limit: 10000, // Fetch a large number to get all logs
        authContext: auth 
      });

      const allLogs = allLogsResponse.logs || [];
      console.log('Total query logs fetched:', allLogs.length);

      // Helper function to parse query type
      const parseQueryType = (queryType) => {
        if (!queryType) return 'Unknown';
        
        const typeMap = {
          'general': 'General Query',
          'technical': 'Technical Support',
          'billing': 'Billing Inquiry',
          'feature': 'Feature Request',
          'bug': 'Bug Report',
          'account': 'Account Issue',
          'other': 'Other'
        };
        
        return typeMap[queryType.toLowerCase()] || queryType;
      };

      // Calculate statistics from ALL logs
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Calculate recent 7 days count from total logs
      const recent7DaysCount = allLogs.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate >= sevenDaysAgo;
      }).length;

      // Calculate monthly count from total logs
      const monthlyCount = allLogs.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate >= thirtyDaysAgo;
      }).length;

      // Calculate query type statistics from total logs
      const queryTypeCounts = {};
      
      allLogs.forEach(log => {
        const queryType = parseQueryType(log.query_type || log.queryType);
        
        if (!queryTypeCounts[queryType]) {
          queryTypeCounts[queryType] = {
            type: queryType,
            count: 0,
            success: 0,
            failed: 0,
            totalConfidence: 0
          };
        }
        
        queryTypeCounts[queryType].count++;
        
        if (log.status && ['success', 'completed'].includes(log.status.toLowerCase())) {
          queryTypeCounts[queryType].success++;
        } else if (log.status && ['failed', 'error'].includes(log.status.toLowerCase())) {
          queryTypeCounts[queryType].failed++;
        }

        if (log.confidence) {
          queryTypeCounts[queryType].totalConfidence += log.confidence;
        }
      });
      
      // Convert to array and sort by count
      const queryTypeStats = Object.values(queryTypeCounts)
        .map(type => ({
          ...type,
          averageConfidence: type.count > 0 ? type.totalConfidence / type.count : 0
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10); // Top 10 query types

      // Process dashboard data
      const processedStats = {
        totalQueries: dashboardData.todayCount || 0,
        completedQueries: dashboardData.todaySuccess || 0,
        pendingQueries: 0, // Calculate from logs if available
        failedQueries: Math.max(0, (dashboardData.todayCount || 0) - (dashboardData.todaySuccess || 0)),
        averageConfidence: 0.85, // Default value, could be calculated from logs
        topQueryTypes: dashboardData.classificationStats && dashboardData.classificationStats.length > 0 
          ? dashboardData.classificationStats 
          : [{ type: 'Top query types', count: stats.totalQueries, value: stats.totalQueries }], // Use classification stats from backend or default
        recentActivity: dashboardData.logs?.slice(0, 5).map(log => {
          return {
            id: log._id || log.id,
            query: log.original_query || log.originalQuery || log.query || 'N/A',
            type: parseQueryType(log.query_type || log.queryType),
            status: log.status || 'unknown',
            time: new Date(log.timestamp).toLocaleString()
          };
        }) || [],
        // Use calculated statistics from total logs
        recent7DaysCount: recent7DaysCount,
        monthlyCount: monthlyCount,
        queryTypeStats: queryTypeStats
      };
      console.log('Processed Query Stats from total logs:', processedStats);

      // Calculate pending queries from logs
      if (dashboardData.logs) {
        processedStats.pendingQueries = dashboardData.logs.filter(
          log => log.status && !['success', 'completed', 'failed', 'error'].includes(log.status.toLowerCase())
        ).length;
      }

      // Process analytics data
      setAnalytics({
        totalQueries: analyticsData.totalQueries || 0,
        totalSuccess: analyticsData.totalHighConfidence || 0,
        totalFailed: analyticsData.totalLowConfidence || 0,
        perDay: analyticsData.perDay || []
      });

      // Generate chart data
      const chartData = generateChartData(allLogs);
      setChartData(chartData);

      setStats(processedStats);
    } catch (err) {
      console.error('Error loading query dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await loadDashboardData();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return <FaCheckCircle className="text-green-500" />;
      case 'pending':
      case 'processing':
        return <FaClock className="text-yellow-500" />;
      case 'failed':
      case 'error':
        return <FaExclamationTriangle className="text-red-500" />;
      default:
        return <FaClock className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'pending':
      case 'processing':
        return 'text-yellow-600 bg-yellow-100';
      case 'failed':
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const generateChartData = (logs) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Daily chart data for last 7 days
    const dailyData = [];
    const dailyLabels = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      dailyLabels.push(dateStr);
      
      const dayLogs = logs.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate.toDateString() === date.toDateString();
      });
      
      dailyData.push(dayLogs.length);
    }
    
    // 7-day bar chart data
    const sevenDayData = [];
    const sevenDayLabels = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      sevenDayLabels.push(dateStr);
      
      const dayLogs = logs.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate.toDateString() === date.toDateString();
      });
      
      sevenDayData.push(dayLogs.length);
    }
    
    // 30-day bar chart data
    const thirtyDayData = [];
    const thirtyDayLabels = [];
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      thirtyDayLabels.push(dateStr);
      
      const dayLogs = logs.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate.toDateString() === date.toDateString();
      });
      
      thirtyDayData.push(dayLogs.length);
    }
    
    // Hourly failed chats data for today
    const hourlyFailedData = [];
    const hourlyLabels = [];
    
    for (let hour = 0; hour < 24; hour++) {
      const hourStr = `${hour.toString().padStart(2, '0')}:00`;
      hourlyLabels.push(hourStr);
      
      const hourLogs = logs.filter(log => {
        const logDate = new Date(log.timestamp);
        const logHour = logDate.getHours();
        const isToday = logDate.toDateString() === today.toDateString();
        const isFailed = log.status && ['failed', 'error'].includes(log.status.toLowerCase());
        
        return isToday && isFailed && logHour === hour;
      });
      
      hourlyFailedData.push(hourLogs.length);
    }
    
    return {
      dailyChart: {
        labels: dailyLabels,
        datasets: [
          {
            label: 'Queries',
            data: dailyData,
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            tension: 0.4,
            fill: true
          }
        ]
      },
      sevenDayBarChart: {
        labels: sevenDayLabels,
        datasets: [
          {
            label: 'Queries (7 Days)',
            data: sevenDayData,
            backgroundColor: 'rgba(99, 102, 241, 0.8)',
            borderColor: 'rgb(99, 102, 241)',
            borderWidth: 1,
            borderRadius: 4
          }
        ]
      },
      thirtyDayBarChart: {
        labels: thirtyDayLabels,
        datasets: [
          {
            label: 'Queries (30 Days)',
            data: thirtyDayData,
            backgroundColor: 'rgba(168, 85, 247, 0.8)',
            borderColor: 'rgb(168, 85, 247)',
            borderWidth: 1,
            borderRadius: 4
          }
        ]
      },
      hourlyFailedChart: {
        labels: hourlyLabels,
        datasets: [
          {
            label: 'Failed Queries',
            data: hourlyFailedData,
            backgroundColor: 'rgba(239, 68, 68, 0.8)',
            borderColor: 'rgb(239, 68, 68)',
            borderWidth: 1
          }
        ]
      }
    };
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pl_prim_color_1"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <FaTimes className="text-4xl mx-auto mb-2" />
            <p className="text-lg font-semibold">Error Loading Dashboard</p>
          </div>
          <p className="text-pl_txt_light_3 mb-4">{error}</p>
          <button
            onClick={loadDashboardData}
            className="px-4 py-2 bg-pl_prim_color_1 text-white rounded-lg hover:bg-pl_prim_color_2 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-pl_txt_light_1 flex items-center gap-3">
          <FaSearch className="text-pl_prim_color_1" />
          Query Classification Dashboard
        </h1>
        <p className="text-pl_txt_light_3 mt-2">
          Overview of query classification activities and performance metrics
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-pl_bg_light_5 rounded-lg p-6 border-l-4 border-l-pl_prim_color_1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-pl_txt_light_3">Today's Queries</p>
              <p className="text-2xl font-bold text-pl_txt_light_1">{stats.totalQueries.toLocaleString()}</p>
            </div>
            <FaSearch className="text-3xl text-pl_prim_color_1" />
          </div>
        </div>

        <div className="bg-pl_bg_light_5 rounded-lg p-6 border-l-4 border-l-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-pl_txt_light_3">Failed</p>
              <p className="text-2xl font-bold text-pl_txt_light_1">{stats.failedQueries.toLocaleString()}</p>


            </div>
            <FaExclamationTriangle className="text-3xl text-red-500" />
          </div>
        </div>
      </div>

      {/* Additional Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-pl_bg_light_5 rounded-lg p-6 border-l-4 border-l-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-pl_txt_light_3">Last 7 Days</p>
              <p className="text-2xl font-bold text-pl_txt_light_1">{stats.recent7DaysCount.toLocaleString()}</p>
            </div>
            <FaGlobe className="text-3xl text-blue-500" />
          </div>
        </div>

        <div className="bg-pl_bg_light_5 rounded-lg p-6 border-l-4 border-l-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-pl_txt_light_3">This Month</p>
              <p className="text-2xl font-bold text-pl_txt_light_1">{stats.monthlyCount.toLocaleString()}</p>
            </div>
            <FaChartBar className="text-3xl text-purple-500" />
          </div>
        </div>

        <div className="bg-pl_bg_light_5 rounded-lg p-6 border-l-4 border-l-indigo-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-pl_txt_light_3">Query Types</p>
              <p className="text-2xl font-bold text-pl_txt_light_1">{stats.queryTypeStats.length}</p>
            </div>
            <FaBrain className="text-3xl text-indigo-500" />
          </div>
        </div>
      </div>

      {/* Charts and Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Analytics Overview */}
        <div className="bg-pl_bg_light_5 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-pl_txt_light_1 mb-4 flex items-center gap-2">
            <FaChartBar />
            Overall Analytics
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-pl_txt_light_1">Total Queries</span>
              <span className="text-sm text-pl_txt_light_3">{analytics.totalQueries.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-pl_txt_light_1">Successful</span>
              <span className="text-sm text-green-600">{analytics.totalSuccess.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-pl_txt_light_1">Failed</span>
              <span className="text-sm text-red-600">{analytics.totalFailed.toLocaleString()}</span>
            </div>
            <div className="w-full bg-pl_border_light_1 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-green-500 to-pl_prim_color_1 h-2 rounded-full"
                style={{ width: `${analytics.totalQueries > 0 ? (analytics.totalSuccess / analytics.totalQueries * 100) : 0}%` }}
              ></div>
            </div>
            <p className="text-xs text-pl_txt_light_3 text-center">
              Success Rate: {analytics.totalQueries > 0 ? ((analytics.totalSuccess / analytics.totalQueries) * 100).toFixed(1) : 0}%
            </p>
          </div>
        </div>

        {/* Classification Stats Section */}
        {stats.topQueryTypes && stats.topQueryTypes.length > 0 && (
          <div className="bg-pl_bg_light_5 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-pl_txt_light_1 mb-4 flex items-center gap-2">
              <FaBrain />
              Classification Statistics
            </h3>
            <div className="space-y-3">
              {stats.topQueryTypes.map((stat, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-6 h-6 bg-pl_prim_color_2 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {index + 1}
                    </div>
                    <span className="text-sm font-medium text-pl_txt_light_1 truncate">
                      {stat.type || stat.name || 'Top query types'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-pl_txt_light_3">{stat.count || stat.value || 0}</span>
                    <div className="w-20 bg-pl_border_light_1 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-pl_prim_color_2 to-pl_prim_color_1 h-2 rounded-full"
                        style={{ width: `${stats.topQueryTypes[0]?.count || stats.topQueryTypes[0]?.value || 1 > 0 ? ((stat.count || stat.value || 0) / (stats.topQueryTypes[0]?.count || stats.topQueryTypes[0]?.value || 1)) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 7-Day and 30-Day Bar Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* 7-Day Bar Chart */}
        <div className="bg-pl_bg_light_5 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-pl_txt_light_1 mb-4 flex items-center gap-2">
            <FaChartBar />
            7 Days Query Activity
          </h3>
          {chartData.sevenDayBarChart ? (
            <div className="h-64">
              <Bar
                data={chartData.sevenDayBarChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        color: '#6B7280'
                      },
                      grid: {
                        color: '#374151'
                      }
                    },
                    x: {
                      ticks: {
                        color: '#6B7280'
                      },
                      grid: {
                        color: '#374151'
                      }
                    }
                  }
                }}
              />
            </div>
          ) : (
            <div className="text-center text-pl_txt_light_3 py-8">
              Loading chart data...
            </div>
          )}
        </div>

        {/* 30-Day Bar Chart */}
        <div className="bg-pl_bg_light_5 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-pl_txt_light_1 mb-4 flex items-center gap-2">
            <FaChartBar />
            30 Days Query Activity
          </h3>
          {chartData.thirtyDayBarChart ? (
            <div className="h-64">
              <Bar
                data={chartData.thirtyDayBarChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        color: '#6B7280'
                      },
                      grid: {
                        color: '#374151'
                      }
                    },
                    x: {
                      ticks: {
                        color: '#6B7280'
                      },
                      grid: {
                        color: '#374151'
                      }
                    }
                  }
                }}
              />
            </div>
          ) : (
            <div className="text-center text-pl_txt_light_3 py-8">
              Loading chart data...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueryDashboard; 